import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './Modal';
import { ProfileIcon } from './ProfileIcon';
import { useAuth } from '../../auth/useAuth';
import { useLanguage } from '../../i18n/useLanguage';

interface ProfilePictureModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  username?: string;
}

/**
 * Modal component to display a full-size profile picture.
 */
export const ProfilePictureModal: React.FC<ProfilePictureModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
}) => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const isOwnProfile = currentUser?.sub === userId;

  // Use the by-user endpoint which resolves the correct extension server-side
  const profilePictureUrl = userId
    ? `/api/users/profile/picture/by-user/${userId}`
    : null;

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Reset state when modal opens/closes or userId changes
  useEffect(() => {
    if (isOpen) {
      setImageError(false);
      setImageLoading(true);
    }
  }, [isOpen, userId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div className="profile-picture-modal">
        <div className="profile-picture-modal-header">
          <h3 className="profile-picture-modal-title">
            {username ? `${username}'s Profile Picture` : 'Profile Picture'}
          </h3>
          <button 
            className="profile-picture-modal-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="profile-picture-modal-content">
          {profilePictureUrl && !imageError ? (
            <>
              {imageLoading && (
                <div className="profile-picture-modal-loading">
                  <ProfileIcon size={64} className="profile-picture-icon" />
                </div>
              )}
              <img
                src={profilePictureUrl}
                alt={username ? `${username}'s profile picture` : 'Profile picture'}
                className="profile-picture-modal-image"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ display: imageLoading ? 'none' : 'block' }}
              />
            </>
          ) : (
            <div className="profile-picture-modal-placeholder">
              <ProfileIcon size={128} className="profile-picture-icon" />
              <p className="profile-picture-modal-placeholder-text">
                No profile picture available
              </p>
            </div>
          )}
        </div>

        {!isOwnProfile && (
          <div className="profile-picture-modal-footer">
            <button
              className="button button--primary button--small"
              onClick={() => {
                onClose();
                navigate(
                  `/messages?userId=${encodeURIComponent(userId)}&username=${encodeURIComponent(username || '')}`,
                );
              }}
            >
              💬 {t('profile.startChat')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
